import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { clickableTextClass } from '@components/RingilComponents/clickableText';

export interface LinkedEntity {
  /** Stable react key — the entity id. */
  id: string;
  /** What the user sees, e.g. FV-000587 or SN-39830. */
  applicationId: string;
  /** Optional detail shown in parentheses after the id, e.g. the supplier name of an SN. */
  note?: string;
  /** Target of the link, already built by useNavigation. */
  to: string;
}

interface ComponentProps {
  label: string;
  links: LinkedEntity[];
  /**
   * Strikes the ids through, for entities that have since been canceled — a merge source, say. The
   * tag then reads as history rather than as something still live.
   */
  strikeThrough?: boolean;
  dataTestId?: string;
}

/**
 * A badge-row tag pointing at other entities: a label plus a comma separated list of links.
 *
 * Used for every "related to" relation on a detail — the shipment's origin notification, the
 * shipments and notifications a merge folded in, the shipment a notification ended up in — so all of
 * them read as one group rather than as several inventions.
 *
 * A list of several starts folded down to its label — the badge row carries a dozen other tags, and
 * a shipment merged from eight others would push all of them off screen. "show" opens it, "hide"
 * puts it back. A single link has nothing to fold, so it is simply shown.
 */
const LinkedEntitiesBadge: React.FC<ComponentProps> = ({ label, links, strikeThrough = false, dataTestId }) => {
  const { tr } = useTranslationLgs();
  const [expanded, setExpanded] = useState(false);

  if (links.length === 0) return null;

  const collapsible = links.length > 1;
  const visibleLinks = collapsible && !expanded ? [] : links;

  return (
    <div
      className="inline-flex flex-wrap items-center gap-1 rounded border border-solid border-content-brandMedium bg-content-brandMedium px-2 py-1 font-dm-sans text-[14px] font-medium leading-none tracking-[-0.14px]"
      data-testid={dataTestId}
    >
      <div className="text-content-secondary">{label}</div>
      {visibleLinks.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-1 gap-y-1">
          {visibleLinks.map((link, index) => (
            <span key={link.id} className="[word-break:break-word]">
              <Link to={link.to} className={clsx(clickableTextClass, strikeThrough && 'line-through')}>
                {link.note ? `${link.applicationId} (${link.note})` : link.applicationId}
              </Link>
              {index < visibleLinks.length - 1 && <span className="text-content-secondary">,</span>}
            </span>
          ))}
        </div>
      )}
      {collapsible && (
        <div
          className={clsx(clickableTextClass, 'text-[12px] font-medium tracking-[-0.12px]')}
          onClick={() => setExpanded(!expanded)}
          data-testid={dataTestId && `${dataTestId}-toggle`}
        >
          {expanded ? tr('LinkedEntitiesBadge.hide', 'hide') : tr('LinkedEntitiesBadge.show', 'show')}
        </div>
      )}
    </div>
  );
};

export default LinkedEntitiesBadge;
